import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { assets } from '../config/assets'

const SWIPE_THRESHOLD = 50

/**
 * Genre carousel for the thematic subscriptions page.
 *
 * - Desktop: vertical carousel (one poster, up/down arrows).
 * - Mobile: horizontal carousel (one poster, left/right arrows, swipeable).
 *
 * Props:
 * - items: array of subscription/genre objects
 * - activeIndex: currently selected index
 * - onSelect: callback(index) when the active item changes
 * - ariaLabel: accessibility label for the carousel region
 */
export default function VerticalGenreCarousel({
  items,
  activeIndex,
  onSelect,
  ariaLabel,
}) {
  const { t } = useTranslation()
  const touchStartX = useRef(null)
  const touchStartY = useRef(null)
  const activeImageRef = useRef(null)
  const [posterHeight, setPosterHeight] = useState(0)

  const goPrev = () => {
    const next = (activeIndex - 1 + items.length) % items.length
    onSelect(next)
  }

  const goNext = () => {
    const next = (activeIndex + 1) % items.length
    onSelect(next)
  }

  const measurePoster = () => {
    if (activeImageRef.current) {
      setPosterHeight(activeImageRef.current.offsetHeight)
    }
  }

  useEffect(() => {
    measurePoster()
  }, [activeIndex])

  useEffect(() => {
    const handleResize = () => measurePoster()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
  }

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return
    const diffX = touchStartX.current - e.changedTouches[0].clientX
    const diffY = touchStartY.current - e.changedTouches[0].clientY

    // Only react to dominant horizontal swipes.
    if (
      Math.abs(diffX) > Math.abs(diffY) &&
      Math.abs(diffX) > SWIPE_THRESHOLD
    ) {
      diffX > 0 ? goNext() : goPrev()
    }

    touchStartX.current = null
    touchStartY.current = null
  }

  if (items.length === 0) return null

  return (
    <div
      className="flex items-center justify-center"
      role="region"
      aria-label={ariaLabel}
    >
      {/* Desktop vertical carousel */}
      <div className="hidden md:flex flex-col items-center gap-4 w-full">
        <button
          type="button"
          onClick={goPrev}
          aria-label={t('carousel.previous')}
          className="appearance-none bg-transparent border-none p-0 cursor-pointer transition-transform active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
        >
          <img
            src={assets.arrowUp}
            alt=""
            draggable={false}
            className="h-6 lg:h-[1.875rem] w-auto drop-shadow-2xl"
          />
        </button>

        <div
          className="relative w-full min-h-[45vh]"
          style={{ height: posterHeight || undefined }}
        >
          <div className="absolute -inset-4 overflow-hidden">
            {items.map((item, idx) => {
              const offset = idx - activeIndex
              const isActive = idx === activeIndex
              return (
                <div
                  key={item.id}
                  className="absolute inset-0 flex items-center justify-center p-4 transition-transform duration-500 ease-out"
                  style={{ transform: `translateY(${offset * 100}%)` }}
                >
                  <img
                    ref={isActive ? activeImageRef : null}
                    src={item.image}
                    alt=""
                    draggable={false}
                    onLoad={isActive ? measurePoster : undefined}
                    className="w-full max-w-[280px] sm:max-w-xs md:max-w-sm lg:max-w-md max-h-[55vh] lg:max-h-[60vh] h-auto object-contain"
                  />
                </div>
              )
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={goNext}
          aria-label={t('carousel.next')}
          className="appearance-none bg-transparent border-none p-0 cursor-pointer transition-transform active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
        >
          <img
            src={assets.arrowDown}
            alt=""
            draggable={false}
            className="h-6 lg:h-[1.875rem] w-auto drop-shadow-2xl"
          />
        </button>
      </div>

      {/* Mobile horizontal carousel */}
      <div
        className="md:hidden relative w-full"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          >
            {items.map((item) => (
              <div
                key={item.id}
                className="w-full flex-shrink-0 flex items-center justify-center px-12"
              >
                <img
                  src={item.image}
                  alt=""
                  draggable={false}
                  className="h-[45vh] w-auto max-w-full object-contain"
                />
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={goPrev}
          aria-label={t('carousel.previous')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 appearance-none bg-transparent border-none p-0 cursor-pointer transition-transform active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
        >
          <img
            src={assets.arrowLeft}
            alt=""
            draggable={false}
            className="h-10 w-auto drop-shadow-2xl"
          />
        </button>

        <button
          type="button"
          onClick={goNext}
          aria-label={t('carousel.next')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 appearance-none bg-transparent border-none p-0 cursor-pointer transition-transform active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
        >
          <img
            src={assets.arrowRight}
            alt=""
            draggable={false}
            className="h-10 w-auto drop-shadow-2xl"
          />
        </button>
      </div>
    </div>
  )
}
